import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { ScenarioSlot, defaultSlots, samplePosting } from '../data/demo';
export type DemoMode = 'cohort' | 'single';
export type InviteStatus = 'NOT STARTED' | 'IN PROGRESS' | 'SUBMITTED' | 'AWAITING REVIEW';
interface DemoValue {
  postingUrl: string;
  postingText: string;
  usedSample: boolean;
  candidateCount: number;
  mode: DemoMode;
  slots: ScenarioSlot[];
  cvName: string | null;
  pipelineComplete: boolean;
  inviteStatuses: InviteStatus[];
  setPostingUrl: (v: string) => void;
  setPostingText: (v: string) => void;
  useSamplePosting: () => void;
  setCandidateCount: (n: number) => void;
  setMode: (m: DemoMode) => void;
  setSlotClass: (slotId: string, klass: ScenarioSlot['klass']) => boolean;
  resetSlots: () => void;
  setCvName: (name: string | null) => void;
  setPipelineComplete: (v: boolean) => void;
  setInviteStatuses: (s: InviteStatus[]) => void;
  offLibrary: boolean;
}
const DemoContext = createContext<DemoValue | null>(null);
export function DemoProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [postingUrl, setPostingUrl] = useState('');
  const [postingText, setPostingText] = useState('');
  const [usedSample, setUsedSample] = useState(false);
  const [candidateCount, setCandidateCount] = useState(3);
  const [mode, setMode] = useState<DemoMode>('cohort');
  const [slots, setSlots] = useState<ScenarioSlot[]>(defaultSlots);
  const [cvName, setCvName] = useState<string | null>(null);
  const [pipelineComplete, setPipelineComplete] = useState(false);
  const [inviteStatuses, setInviteStatuses] = useState<InviteStatus[]>(['NOT STARTED', 'NOT STARTED', 'NOT STARTED']);
  const useSamplePosting = useCallback(() => {
    setPostingUrl(samplePosting.url);
    setPostingText('');
    setUsedSample(true);
  }, []);

  // The clean-case floor is enforced here, not in the UI — the refusal is part of the instrument.
  const setSlotClass = useCallback((slotId: string, klass: ScenarioSlot['klass']) => {
    let accepted = true;
    setSlots(prev => {
      const next = prev.map(s => s.id === slotId ? {
        ...s,
        klass
      } : s);
      const cleanCount = next.filter(s => s.klass === 'CLEAN').length;
      if (cleanCount < 2) {
        accepted = false;
        return prev;
      }
      return next;
    });
    return accepted;
  }, []);
  const resetSlots = useCallback(() => setSlots(defaultSlots), []);
  const offLibrary = useMemo(() => {
    if (usedSample) return false;
    const raw = `${postingUrl} ${postingText}`.toLowerCase();
    if (!raw.trim()) return false;
    return !/financial|controller|account|close|ledger/.test(raw);
  }, [postingUrl, postingText, usedSample]);
  const value: DemoValue = {
    postingUrl,
    postingText,
    usedSample,
    candidateCount,
    mode,
    slots,
    cvName,
    pipelineComplete,
    inviteStatuses,
    setPostingUrl,
    setPostingText,
    useSamplePosting,
    setCandidateCount,
    setMode,
    setSlotClass,
    resetSlots,
    setCvName,
    setPipelineComplete,
    setInviteStatuses,
    offLibrary
  };
  return <DemoContext.Provider value={value} data-id="element-329" data-mp="39wp17cbkgsqu">{children}</DemoContext.Provider>;
}
export function useDemo() {
  const ctx = useContext(DemoContext);
  if (!ctx) throw new Error('useDemo must be used inside DemoProvider');
  return ctx;
}